import { Box, Button, TextField } from '@mui/material';
import React, { useEffect } from 'react';
import CaseCommentEditor from '../editor/CaseCommentEditor';
import { CANtrackComment, CANtrackCase } from '../../types/comment';
import { AddIcon, RemoveIcon } from 'components/icons';
import useCommentEditor from '../../hooks/useCommentEditor';

const CaseCommentManager: React.FC<{
	setComments: React.Dispatch<React.SetStateAction<CANtrackComment[]>>;
	commentToCopy?: CANtrackComment;
	resetCommentToCopy: () => void;
	userId?: string; // used to create comments for a different user than the logged in user - used in timesheets
	contactId?: string; // used to create comments for a different user than the logged in user - used in timesheets
	defaultCase?: CANtrackCase; // pre-fill and lock the case field when adding from a case context
	searchTerm?: string;
	onSearchChange?: (value: string) => void;
}> = (props) => {
	const [open, setOpen] = React.useState(false);
	const {
		errors,
		formState,
		handleChange,
		handleSaveComment,
		handleResetComment,
	} = useCommentEditor(
		props.setComments,
		props.commentToCopy,
		props.userId,
		props.contactId,
		props.defaultCase,
	);

	useEffect(() => {
		if (props.commentToCopy) {
			setOpen(true);
		}
	}, [props.commentToCopy]);

	useEffect(() => {
		if (!open) {
			// reset states when closing the editor
			resetStates();
		}
	}, [open]);

	const resetStates = () => {
		handleResetComment();
		props.resetCommentToCopy();
	};

	return (
		<>
			<Box
				sx={{
					display: 'flex',
					flexDirection: { xs: 'column', sm: 'row' },
					justifyContent: 'space-between',
					gap: 1,
				}}
			>
				<Button
					variant="contained"
					onClick={() => setOpen(!open)}
					startIcon={open ? <RemoveIcon /> : <AddIcon />}
					size="small"
					sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
				>
					New Comment
				</Button>
				<TextField
					label="Search"
					placeholder="Filter by client, case, or summary..."
					value={props.searchTerm ?? ''}
					onChange={(e) => props.onSearchChange?.(e.target.value)}
					sx={{ width: { xs: '100%', sm: 300 } }}
					size="small"
				/>
			</Box>
			{open && (
				<CaseCommentEditor
					errors={errors}
					formState={formState}
					handleChange={handleChange}
					handleSave={handleSaveComment}
					handleReset={resetStates}
					handleDelete={() => {}} // can't delete a new comment
					lockedCase={!!props.defaultCase}
				/>
			)}
		</>
	);
};

export default CaseCommentManager;
